import {
  getModelReasoningCapability,
  type ReasoningEffort,
} from '@kanbu/schema/contracts';
import { useLingui } from '@lingui/react/macro';
import { Select, SelectItem } from '@utima/ui';
import { FormControl } from '@utima/ui-informed';
import { useFieldState, useFormApi } from 'informed';
import { useEffect, useRef } from 'react';

const DEFAULT_OPTION = 'default';

/**
 * Reasoning effort of the main agent, shown right below the model picker and
 * only for models listed in `MODEL_REASONING_CAPABILITIES` — every level
 * offered there was accepted by the provider. "Default" is stored as `null`
 * so it clears an earlier choice when `agentConfig` is merged on update, and a
 * model change resets it so a level picked for one model never carries over
 * to another.
 */
export function ReasoningEffortField() {
  const { t } = useLingui();
  const formApi = useFormApi();
  const model = useFieldState('model').value as string | undefined;
  const previousModel = useRef(model);
  const capability = getModelReasoningCapability(model);

  useEffect(() => {
    if (previousModel.current === model) {
      return;
    }

    const modelWasSelected = previousModel.current !== undefined;

    previousModel.current = model;

    if (modelWasSelected) {
      formApi.setValue('agentConfig.reasoningEffort', null);
    }
  }, [formApi, model]);

  if (!capability) {
    return null;
  }

  const labels: Record<ReasoningEffort, string> = {
    minimal: t({ message: 'Minimal', context: 'Reasoning effort level' }),
    low: t({ message: 'Low', context: 'Reasoning effort level' }),
    medium: t({ message: 'Medium', context: 'Reasoning effort level' }),
    high: t({ message: 'High', context: 'Reasoning effort level' }),
  };
  const defaultLabel = labels[capability.defaultEffort];

  return (
    <FormControl
      name='agentConfig.reasoningEffort'
      label={t`Reasoning effort`}
      type='select'
      keepState
      showOptional={false}
      tooltip={t`How long the model may think before it answers. Higher levels can improve calculations and multi-step requests, but answers take longer and cost more. Price estimates in the model list assume the default level.`}
      render={({ userProps, ref, fieldApi, fieldState }) => {
        const { disabled, readOnly, id } = userProps;
        const value = capability.efforts.find(
          effort => effort === fieldState.value,
        );

        return (
          <Select
            ref={ref}
            id={id}
            value={value ?? DEFAULT_OPTION}
            disabled={disabled || readOnly}
            onValueChange={next => {
              fieldApi.setTouched(true);
              fieldApi.setValue(next === DEFAULT_OPTION ? null : next);
            }}
          >
            <SelectItem value={DEFAULT_OPTION}>
              {t`Default (${defaultLabel})`}
            </SelectItem>
            {capability.efforts.map(effort => (
              <SelectItem key={effort} value={effort}>
                {labels[effort]}
              </SelectItem>
            ))}
          </Select>
        );
      }}
    />
  );
}
